// Табът „Движения" на складовата карта: чипове по вид движение с броячи (един
// избран — „Всички" по подразбиране), сумите Приход/Разход на показаното и
// Excel, под тях таблицата с началното и крайното салдо. Документът на реда е
// „Доставка №25" / „Отчет №4"; при замяна — родителският документ и бележка
// „⇄ вместо Кафе A" / „⇄ покрито от Кафе B".
import { useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import type { StockCard, StockCardEntry } from "../../../../api/inventoryStockCard";
import { exportToExcel } from "../../../../utils/excelExport";
import { formatQty } from "../../components/qty";
import { fmtDate } from "../../components/dateTime";
import { SECONDARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import type { Period } from "../../components/period/periodPresets";
import { StockCardMovementsTable } from "./StockCardMovementsTable";
import {
  cardExcelRows,
  cardIsConsistent,
  cardNegativeNote,
  isNegativeBalance,
  cardTotals,
  cardTypeCounts,
  filterCardEntries,
  shortDateFormat,
  type CardTypeFilter,
} from "./stockCardModel";

type Props = {
  card: StockCard | undefined;
  isLoading: boolean;
  isRefreshing: boolean; // нов период/продукт се зарежда върху старите редове
  period: Period;
  productId: number;
  productName: string;
  warehouseName: string;
  decimals: number;
  onOpenDocument: (entry: StockCardEntry) => void;
  // Чипът, с който картата се отваря (напр. „Отчет" от „Продадени продукти");
  // важи, докато потребителят не избере друг, и само ако го има в периода
  defaultType?: CardTypeFilter;
};

const CHIP_CLASS =
  "inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[12.5px] leading-[normal] outline-none focus-visible:shadow-[0_0_0_2px_var(--color-brand)]";

export const StockCardMovements = ({
  card,
  isLoading,
  isRefreshing,
  period,
  productId,
  productName,
  warehouseName,
  decimals,
  onOpenDocument,
  defaultType = "all",
}: Props) => {
  const { t } = useTranslation();
  // Филтърът е на продукта: ↑/↓ към друг продукт го връща на подразбиращия се
  const [typeFilter, setTypeFilter] = useState<{ productId: number; type: CardTypeFilter } | null>(null);
  const chosenType = typeFilter?.productId === productId ? typeFilter.type : null;

  if (!card) {
    return (
      <div className="flex flex-1 items-center justify-center p-10">
        {isLoading ? <Spin /> : <span className="text-[13px] text-body">{t("inventoryPage.errLoad")}</span>}
      </div>
    );
  }

  const counts = cardTypeCounts(card.entries);
  // Подразбиращият се вид важи само ако го има в периода — иначе „Всички"
  const fallbackType: CardTypeFilter = counts.some((item) => item.type === defaultType) ? defaultType : "all";
  const activeType: CardTypeFilter = chosenType ?? fallbackType;
  const entries = filterCardEntries(card.entries, activeType);
  const totals = cardTotals(entries);
  const unit = card.unit ? ` ${card.unit}` : "";
  const dateFormat = shortDateFormat(period);

  const documentLabelOf = (entry: StockCardEntry) =>
    t("inventoryPage.scDocNumber", {
      label: t(`inventoryPage.scDoc_${entry.documentType}`),
      number: entry.documentNumber,
    });
  const noteOf = (entry: StockCardEntry) => {
    const counterpart = entry.counterpartProductName ?? "—";
    if (entry.substitutionRole === "donor") return t("inventoryPage.postingInsteadOf", { name: counterpart });
    if (entry.substitutionRole === "recipient") return t("inventoryPage.scCoveredBy", { name: counterpart });
    // инвентаризация с приготвени рецепти: колко от преброеното е в тях
    const inRecipes = entry.inRecipes
      ? t("inventoryPage.sr6DocInRecipes", { qty: `${formatQty(entry.inRecipes, decimals)}${unit}` })
      : null;
    if (entry.reason || inRecipes) return [entry.reason, inRecipes].filter(Boolean).join(" · ");
    if (entry.batchNumber || entry.expiryDate)
      return [
        entry.batchNumber && t("inventoryPage.scBatchNote", { batch: entry.batchNumber }),
        entry.expiryDate && t("inventoryPage.scExpiryNote", { date: fmtDate(entry.expiryDate) }),
      ]
        .filter(Boolean)
        .join(" · ");
    return "";
  };

  // Минусът от продажби: наличността се показва като 0, а бележката казва колко
  // е продадено без наличност и колко от прихода е покрило минуса
  const quantityWithUnit = (quantity: number) => `${formatQty(quantity, decimals)}${unit}`;
  const negativeNoteOf = (entry: StockCardEntry) => {
    const negative = cardNegativeNote(entry);
    if (!negative) return null;
    if (negative.kind === "covered")
      return {
        text: t("inventoryPage.scCoveredNegative", { qty: formatQty(negative.quantity, decimals) }),
        danger: false,
      };
    const key = negative.kind === "sold" ? "inventoryPage.scSoldWithoutStock" : "inventoryPage.scWrittenOffWithoutStock";
    return { text: t(key, { qty: quantityWithUnit(negative.quantity) }), danger: true };
  };
  const balanceNoteOf = (balance: number) =>
    isNegativeBalance(balance) ? t("inventoryPage.scSoldWithoutStock", { qty: quantityWithUnit(-balance) }) : null;
  const excelNoteOf = (entry: StockCardEntry) =>
    [noteOf(entry), negativeNoteOf(entry)?.text].filter(Boolean).join(" · ");

  const openingLabel = `${t("inventoryPage.scOpening")} · ${period.from.format("DD.MM.YYYY")}`;
  const closingLabel = `${t("inventoryPage.scClosing")} · ${period.to.format("DD.MM.YYYY")}`;

  const exportXls = () =>
    exportToExcel({
      filename: "skladova-karta",
      sheetName: t("inventoryPage.scTitle"),
      rows: cardExcelRows(card, entries, period, {
        opening: t("inventoryPage.scOpening"),
        closing: t("inventoryPage.scClosing"),
        documentOf: documentLabelOf,
        noteOf: excelNoteOf,
      }),
      columns: [
        { header: t("inventoryPage.colDate"), value: (row) => row.date, width: 18 },
        { header: `${t("inventoryPage.colDocument")} · ${productName} · ${warehouseName}`, value: (row) => row.document, width: 36 },
        { header: t("inventoryPage.scColIn"), value: (row) => row.quantityIn },
        { header: t("inventoryPage.scColOut"), value: (row) => row.quantityOut },
        { header: t("inventoryPage.colAvailable"), value: (row) => row.balance },
        { header: t("inventoryPage.scColPrice"), value: (row) => row.unitPrice },
        { header: t("inventoryPage.scColUser"), value: (row) => row.createdBy, width: 20 },
        { header: t("inventoryPage.scColNote"), value: (row) => row.note, width: 30 },
      ],
    });

  const chip = (type: CardTypeFilter, label: string, count: number) => {
    const active = activeType === type;
    return (
      <button
        key={type}
        type="button"
        role="radio"
        aria-checked={active}
        onClick={() => setTypeFilter({ productId, type })}
        className={`${CHIP_CLASS} ${
          active
            ? "border-brand/45 bg-brand-tint font-semibold text-brand-deep-ink"
            : "border-shade-3 bg-surface text-body hover:border-shade-5"
        }`}
      >
        {label}
        <span className="font-sheet text-[11.5px] opacity-80">{count}</span>
      </button>
    );
  };

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${isRefreshing ? "opacity-60" : ""}`}>
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-shade-2 px-4 py-2.5">
        <div role="radiogroup" aria-label={t("inventoryPage.scTypeFilterAria")} className="flex flex-wrap gap-1.5">
          {chip("all", t("inventoryPage.scTypeAll"), card.entries.length)}
          {counts.map(({ type, count }) => chip(type, t(`inventoryPage.scDoc_${type}`), count))}
        </div>
        <div className="flex-1" />
        <span className="whitespace-nowrap font-sheet text-[12.5px] tabular-nums text-body">
          <span className="text-ok-deep">+{formatQty(totals.quantityIn, decimals)}</span>
          {" · "}
          <span className="text-danger-ink">−{formatQty(totals.quantityOut, decimals)}</span>
          {unit}
        </span>
        <button
          type="button"
          onClick={exportXls}
          disabled={!card.entries.length}
          className={`${SECONDARY_BUTTON_CLASS} !px-2.5 !py-1.5 !text-[12.5px]`}
        >
          {t("inventoryPage.scExcel")}
        </button>
      </div>
      {!cardIsConsistent(card) && activeType === "all" && (
        <div className="shrink-0 border-b border-shade-2 bg-danger-tint px-4 py-2 text-[12.5px] leading-[1.4] text-danger-ink">
          {t("inventoryPage.scInconsistent")}
        </div>
      )}
      <StockCardMovementsTable
        card={card}
        entries={entries}
        decimals={decimals}
        dateFormat={dateFormat}
        openingLabel={openingLabel}
        closingLabel={closingLabel}
        emptyText={
          entries.length
            ? null
            : t(card.entries.length ? "inventoryPage.scEmptyFiltered" : "inventoryPage.scEmptyPeriod")
        }
        documentLabelOf={documentLabelOf}
        noteOf={noteOf}
        negativeNoteOf={negativeNoteOf}
        balanceNoteOf={balanceNoteOf}
        onOpenDocument={onOpenDocument}
      />
    </div>
  );
};

export default StockCardMovements;
